import clsx from 'clsx';
import type { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import Chip from '@mui/material/Chip';

import capitalize from 'utils/capitalize';

const useStyles = makeStyles((theme: Theme) => ({
  small: {
    fontSize: 14,
    maxWidth: 92,
    minWidth: 92,
  },
  medium: {
    fontSize: 16,
  },
  root: {
    width: '100%',
    maxWidth: 132,
    minWidth: 132,
    height: '100%',
    borderRadius: 100,
    padding: '8px 21px',
  },
  square: {
    width: '100%',
    maxWidth: 32,
    minWidth: 32,
    height: '100%',
    maxHeight: 32,
    minHeight: 32,
    fontSize: 16,
    borderRadius: 4,
  },
  labelSquare: {
    textOverflow: 'initial',
  },
  labelNormal: {
    overflow: 'visible',
  },
  colorDefault: {
    backgroundColor: theme.palette.darkGrey[5],
    '&:hover, &:focus, &:active': {
      backgroundColor: theme.palette.darkGrey[5],
      boxShadow: 'none',
    },
  },
  colorSuccess: {
    backgroundColor: theme.palette.success.light,
    '&:hover, &:focus, &:active': {
      backgroundColor: theme.palette.success.light,
      boxShadow: 'none',
    },
  },
  colorError: {
    backgroundColor: theme.palette.error.light,
    '&:hover, &:focus, &:active': {
      backgroundColor: theme.palette.error.light,
      boxShadow: 'none',
    },
  },
  colorErrorInherit: {
    backgroundColor: theme.palette.orange.light,
  },
  colorWarning: {
    backgroundColor: theme.palette.warning.light,
    '&:hover, &:focus, &:active': {
      backgroundColor: theme.palette.warning.light,
      boxShadow: 'none',
    },
  },
  colorInfo: {
    backgroundColor: theme.palette.malibu.main,
  },
  colorAccepted: {
    backgroundColor: theme.palette.warning.light,
    '&:hover, &:focus, &:active': {
      backgroundColor: theme.palette.warning.light,
      boxShadow: 'none',
    },
  },
  colorDenied: {
    backgroundColor: theme.palette.orange.light,
  },
  colorFinished: {
    backgroundColor: theme.palette.error.light,
  },
  colorClosed: {
    backgroundColor: theme.palette.error.light,
  },
  colorOpen: {
    backgroundColor: theme.palette.success.light,
  },
  colorPaid: {
    backgroundColor: theme.palette.lightBlue.light,
    '&:hover, &:focus, &:active': {
      backgroundColor: theme.palette.lightBlue.light,
      boxShadow: 'none',
    },
  },
}));

interface Props {
  label: string;
  className?: string;
  variant?:
    | 'default'
    | 'success'
    | 'error'
    | 'errorInherit'
    | 'warning'
    | 'info'
    | 'accepted'
    | 'denied'
    | 'finished'
    | 'open'
    | 'paid'
    | 'closed'
    | string;
  square?: boolean;
  size?: 'medium' | 'small';
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}
function ChipStatus({ className, variant = 'default', square = false, label, size = 'medium', onClick }: Props) {
  const classes = useStyles();

  return (
    <Chip
      className={clsx(
        classes[size],
        classes[variant],
        {
          [classes[`color${capitalize(variant)}`]]: variant,
        },
        className,
      )}
      classes={{
        root: square ? classes.square : classes.root,
        label: square ? classes.labelSquare : classes.labelNormal,
      }}
      label={label}
      onClick={onClick ? (e) => onClick && onClick(e) : undefined}
      size={size}
    />
  );
}

export default ChipStatus;
